// DayCloseForm.jsx
// ✅ Flow:
// 1) Create Day Close (POST CLOSE_DAY)
// 2) Close Day Session (PUT CLOSE_DAY)
// 3) Save Z-Report context in sessionStorage
// 4) Navigate to Z-Report route (it will auto-open modal there)
// 5) After print on ZReport page → user clicks Continue → Logout

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../pages/cashierMenu.css';
import ApiService from '../../../services/apiService';
import { toast } from 'react-toastify';
import { connect, useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import moment from 'moment';
import { getUserId, getUserName } from '../../../../Redux/selecters';
import { logout } from '../../../../Redux/actions';
import { CashEnums } from '../../../../Enums/config';
import { generateUniqueVoucherNumber } from '../../../services/globalService';

// session key used by ZReport page to auto-load + show modal
const ZREPORT_CTX_KEY = '__ZREPORT_CTX__';

// ✅ change this to your real Z report route
// Based on your routes pasted, ZReport is inside Admin reports:
// <Route path="reports/zreport" element={<ZReport />} />
const Z_REPORT_ROUTE = '/Cashier/Z-Report';

const DayCloseForm = ({ Currency }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userId = useSelector(getUserId);
  const userName = useSelector(getUserName);

  const [Currencies, setCurrency] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [noteQuantities, setNoteQuantities] = useState({});
  const [fillQuantities, setFillQuantities] = useState({});
  const [loading, setLoading] = useState(false);

  const [emp, setEmp] = useState(null);
  const [counterInfo, setCounterInfo] = useState(null);

  const currencyName = Currency?.currency?.name;

  const Shift = useMemo(
    () => [
      { value: '1', label: 'Morning Shift' },
      { value: '2', label: 'Evening Shift' },
      { value: '3', label: 'Night Shift' },
    ],
    [],
  );

  const [formData, setFormData] = useState({
    shift: '',
    note: '',
    digitalAmount: 0,
    counter: { value: '', label: '' },
  });

  // -------------------- helpers --------------------
  const safeParseCounter = () => {
    try {
      const raw = localStorage.getItem('Counter');
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const sumFrom = (mapObj) =>
    Object.entries(mapObj).reduce((total, [val, qty]) => {
      const v = Number(val);
      const q = Number(qty) || 0;
      if (!Number.isFinite(v)) return total;
      return total + v * q;
    }, 0);

  const separateAndCountValues = (denominations) => {
    const filtered = denominations?.find((c) => c?.default === true);
    return (
      filtered?.currencyDenomination
        ?.filter((d) => Number.isFinite(Number(d?.value)))
        ?.map((d) => ({
          currencyDenominationId: d?.currencyDenominationId,
          value: Number(d?.value),
          count: d?.count || 0,
          type: d?.type, // 0 coin, 1 note, 2 fill
        })) || []
    );
  };

  const denoms = useMemo(() => separateAndCountValues(Currencies), [Currencies]);
  const coinDenominations = useMemo(() => denoms.filter((d) => d.type === 0), [denoms]);
  const cashDenominations = useMemo(() => denoms.filter((d) => d.type === 1), [denoms]);
  const fillDenominations = useMemo(() => denoms.filter((d) => d.type === 2), [denoms]);

  useEffect(() => {
    // init default counts
    const initial = {};
    denoms.forEach((d) => {
      initial[d.value] = d.count || 0;
    });
    setQuantities(initial);
  }, [denoms]);

  const totalCoins = sumFrom(quantities);
  const totalNotes = sumFrom(noteQuantities);
  const totalFills = sumFrom(fillQuantities);

  const grandTotalAmount = useMemo(() => {
    return ((Number(formData?.digitalAmount) || 0) + totalCoins + totalNotes + totalFills).toFixed(2);
  }, [formData?.digitalAmount, totalCoins, totalNotes, totalFills]);

  // -------------------- load currency + employee --------------------
  useEffect(() => {
    GetCurrency();
    getEmployee();

    const counter = safeParseCounter();
    if (counter) {
      setCounterInfo(counter);
      setFormData((p) => ({
        ...p,
        counter: { value: counter?.counterId, label: counter?.counterName || `Till-${counter?.counterId}` },
        shift: counter?.shiftId ? String(counter.shiftId) : p.shift,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const GetCurrency = async () => {
    try {
      const api = new ApiService();
      const res = await api.get('GET_CURRENCY');
      setCurrency(res?.data?.result || []);
    } catch (e) {
      console.error(e);
      toast.error('Error fetching currency data');
    }
  };

  const getEmployee = async () => {
    try {
      const api = new ApiService();
      const res = await api.getApiParamater('GET_EMPLOYEE', `?UserId=${userId}`);
      setEmp(res?.data?.result?.[0] || null);
    } catch (e) {
      console.error(e);
      toast.error('Error fetching employee');
    }
  };

  // -------------------- quantity handlers --------------------
  const increment = (setter, key) => setter((p) => ({ ...p, [key]: (p[key] || 0) + 1 }));
  const decrement = (setter, key) => setter((p) => ({ ...p, [key]: Math.max((p[key] || 0) - 1, 0) }));

  const handleShiftChange = (opt) => setFormData((p) => ({ ...p, shift: opt?.value || '' }));
  const handleInputChange = (e) => setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  // ✅ Save z-report context so ZReport auto-opens
  const goToZReportAfterDayClose = ({ counterId, shiftId, dayId }) => {
    const today = moment().format('YYYY-MM-DD');

    const ctx = {
      flow: 'DAY_CLOSE',
      autoOpen: true,
      logoutAfter: true,
      fromDate: today,
      toDate: today,
      counterId: Number(counterId),
      shiftId: Number(shiftId),
      dayId: Number(dayId),
      userId: Number(userId),
      createdAt: new Date().toISOString(),
    };

    sessionStorage.setItem(ZREPORT_CTX_KEY, JSON.stringify(ctx));
    navigate(Z_REPORT_ROUTE, { replace: true });
  };

  // ✅ FINAL: call DayClose then redirect to z report
  const CreateDayClose = async () => {
    try {
      setLoading(true);

      const counter = safeParseCounter();
      if (!counter?.counterId) {
        toast.error('Counter not found in localStorage.');
        return;
      }

      // Build denomination session payload
      const asTotalLine = (faceValue, qty, type, currencyDenominationId) => ({
        currencyId: currencyDenominationId,
        value: Number(faceValue) * Number(qty || 0),
        type,
        sessionType: Number(counter?.shiftId) || 0,
        daySessionId: Number(counter?.dayId) || 0,
        shiftSessionId: Number(counter?.shiftId) || 0,
      });

      const currencyDenomination = [];
      denoms.forEach((d) => {
        const face = d.value;
        if (d.type === 0) {
          const qty = Number(quantities[face] || 0);
          if (qty > 0) currencyDenomination.push(asTotalLine(face, qty, 0, d.currencyDenominationId));
        }
        if (d.type === 1) {
          const qty = Number(noteQuantities[face] || 0);
          if (qty > 0) currencyDenomination.push(asTotalLine(face, qty, 1, d.currencyDenominationId));
        }
        if (d.type === 2) {
          const qty = Number(fillQuantities[face] || 0);
          if (qty > 0) currencyDenomination.push(asTotalLine(face, qty, 2, d.currencyDenominationId));
        }
      });

      if (currencyDenomination.length === 0) {
        toast.error('Please enter currency denominations.');
        return;
      }
      const api = new ApiService();

      // ✅ (2) PUT CLOSE_DAY (session)
      const closeBody = {
        dayId: Number(counter?.dayId) || 0,
        openedBy: Number(userId) || 0,
        closedBy: Number(userId) || 0,
        openDateTime: counter?.counterOpenTime || new Date().toISOString(),
        closeDateTime: new Date().toISOString(),
        status: 'Closed',
        currencyDenomination,
        shiftId: Number(counter?.shiftId) || 0,
        counterId: Number(counter?.counterId) || 0,
      };

      const cRes = await api.put('CLOSE_DAY', closeBody);

      if (cRes?.status === 200 || cRes?.status === 201) {
        toast.success('Day closed successfully');

        // ✅ (3) redirect to z report and auto open modal
        goToZReportAfterDayClose({
          counterId: counter.counterId,
          shiftId: counter.shiftId,
          dayId: counter.dayId,
        });
      } else {
        toast.error('Failed to close day session');
      }
    } catch (e) {
      console.error(e);
      toast.error('Error while closing the day');
    } finally {
      setLoading(false);
    }
  };

  // -------------------- UI --------------------
  return (
    <div className="p-lg-5">
      <div className="p-lg-5">
        <div className="row">
          <div className="col-lg-4 col-sm-4">
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="pb-lg-5">
                <div className="form-group row justify-content-between">
                  <div className="col-md-6 col-lg-6">
                    <label className="form-label">User</label>
                    <input type="text" className="p-2 form-control" value={userName} disabled />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Shift</label>
                    <Select
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isFocused ? '#F5F5F5' : null,
                          color: '#000',
                          ':hover': { backgroundColor: '#F5F5F5', color: '#000' },
                        }),
                      }}
                      value={Shift.find((s) => s.value === formData.shift) || null}
                      onChange={handleShiftChange}
                      options={Shift}
                      menuPortalTarget={document.body}
                    />
                  </div>
                </div>

                <div className="form-group row mt-3 justify-content-between">
                  <div className="col-md-6">
                    <label className="form-label">Amount</label>
                    <input type="text" className="p-2 form-control" value={grandTotalAmount} disabled />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Date</label>
                    <input type="date" className="p-2 form-control" value={moment().format('YYYY-MM-DD')} disabled />
                  </div>
                </div>

                <div className="form-group row mt-3 justify-content-between">
                  <div className="col-md-6 col-lg-6">
                    <label className="form-label">Company</label>
                    <input className="p-2 form-control" value={emp?.restaurantName || ''} disabled />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Branch</label>
                    <input className="p-2 form-control" value={emp?.branchName || ''} disabled />
                  </div>
                </div>

                <div className="form-group row mt-3 justify-content-between">
                  <div className="col-md-6">
                    <label className="form-label">Counter</label>
                    <input className="p-2 form-control" value={counterInfo?.counterName || formData?.counter?.label || ''} disabled />
                  </div>

                  <div className="col-md-6 col-lg-6">
                    <label className="form-label">Remarks</label>
                    <textarea className="p-2 form-control" name="note" value={formData.note} onChange={handleInputChange} />
                  </div>
                </div>

                <div className="form-group row mt-3 justify-content-between">
                  <div className="col-md-6">
                    <label className="form-label">Digital Amount</label>
                    <input type="number" className="p-2 form-control" name="digitalAmount" value={formData.digitalAmount} onChange={handleInputChange} />
                  </div>
                </div>
              </div>

              <div className="row mt-4">
                <div className="end d-flex gap-2">
                  <button className="addItem-btn" type="button" onClick={CreateDayClose} disabled={loading}>
                    {loading ? 'Saving…' : 'Save & Show Z Report'}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Notes */}
          {cashDenominations?.length > 0 && (
            <div className="col">
              <div>Cash Denominations</div>
              <div className="borderMain mt-3 p-4">
                {cashDenominations.map((item, idx) => (
                  <div className="row p-2 my-3 innerBorder" key={idx}>
                    <div className="col-3">{item.value} X</div>
                    <div className="col-6 d-flex justify-content-between">
                      <div className="vr"></div>
                      <div className="quantity-control d-flex justify-content-around center">
                        <div onClick={() => decrement(setNoteQuantities, item.value)} className="addbtn center">
                          -
                        </div>
                        <div className="center px-2">{noteQuantities[item.value] || 0}</div>
                        <div onClick={() => increment(setNoteQuantities, item.value)} className="addbtn center">
                          +
                        </div>
                      </div>
                      <div className="vr"></div>
                    </div>
                    <div className="col-3">{item.value * (noteQuantities[item.value] || 0)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Coins */}
          {coinDenominations?.length > 0 && (
            <div className="col">
              <div>Coin Denominations</div>
              <div className="borderMain mt-3 p-4">
                {coinDenominations.map((item, idx) => (
                  <div className="row p-2 my-3 innerBorder" key={idx}>
                    <div className="col-3">{item.value} X</div>
                    <div className="col-6 d-flex justify-content-between">
                      <div className="vr"></div>
                      <div className="quantity-control d-flex justify-content-around center">
                        <div onClick={() => decrement(setQuantities, item.value)} className="addbtn center">
                          -
                        </div>
                        <div className="center px-2">{quantities[item.value] || 0}</div>
                        <div onClick={() => increment(setQuantities, item.value)} className="addbtn center">
                          +
                        </div>
                      </div>
                      <div className="vr"></div>
                    </div>
                    <div className="col-3">{item.value * (quantities[item.value] || 0)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fills */}
          {fillDenominations?.length > 0 && (
            <div className="col">
              <div>Cents Denominations</div>
              <div className="borderMain mt-3 p-4">
                {fillDenominations.map((item, idx) => (
                  <div className="row p-2 my-3 innerBorder" key={idx}>
                    <div className="col-3">{item.value} X</div>
                    <div className="col-6 d-flex justify-content-between">
                      <div className="vr"></div>
                      <div className="quantity-control d-flex justify-content-around center">
                        <div onClick={() => decrement(setFillQuantities, item.value)} className="addbtn center">
                          -
                        </div>
                        <div className="center px-2">{fillQuantities[item.value] || 0}</div>
                        <div onClick={() => increment(setFillQuantities, item.value)} className="addbtn center">
                          +
                        </div>
                      </div>
                      <div className="vr"></div>
                    </div>
                    <div className="col-3">{(item.value * (fillQuantities[item.value] || 0)).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStatetoProps = (state) => state;
export default connect(mapStatetoProps)(DayCloseForm);
